import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { interval, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ShellNavService } from '../shell-nav.service';
import { NotificationDialogComponent } from '../../notification-dialog/notification-dialog.component';
import { NotificationService } from '../../services/notification.service';
import { ProjectService } from '../../services/project.service';
import { User } from '../../model/user';
import { getStoredUser } from '../../helpers/user-storage.util';

/**
 * Vendor top strip: hamburger (drawer toggle, <=900px) + mobile logo + address
 * search + notifications bell.
 *
 * Search and notifications are migrated verbatim from the retired PA
 * `page-header` (`header.component.ts`) — same ProjectService/NotificationService
 * calls, same 5-min polling, same result shape — just re-homed into the CONNECT
 * 2.0 shell. The inline "X" clear control reproduces what `info-navigation`'s
 * "Clear Search" button used to do (`ProjectService.clearSearchData` +
 * `toggleClearSearchButton(false)`), now that info-navigation is retired.
 */
@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatBadgeModule,
    NotificationDialogComponent,
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent implements OnInit, OnDestroy {
  protected readonly nav = inject(ShellNavService);

  private readonly projectService = inject(ProjectService);
  private readonly notificationService = inject(NotificationService);
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);

  private readonly destroy$ = new Subject<void>();
  private user: User = {};

  searchTerm: string = '';
  showClearSearchButton: boolean = false;

  // The address search filters project data, so it's only shown on the project
  // list pages (Dashboard / Invitations / Pending=proposals / Active / Project
  // History) — hidden on the account pages (My Account, Services & Zones,
  // Documents), Settings, Contact, Receivables, and detail/workspace routes.
  showSearch = false;
  private readonly searchRoutes = [
    'dashboard',
    'invitations',
    'proposals',
    'active',
    'project-history',
  ];

  notificationsActive: boolean = false;
  readNotifications: any = [];
  unreadNotifications: any = [];

  // The vendor's project feed, handed to the notifications panel so a click
  // resolves to where the project currently lives (see resolveNotificationRoute).
  projects: any[] = [];

  ngOnInit(): void {
    this.user = getStoredUser();
    this.fetchNotifications();

    // Reuse the cached project feed (ReplaySubject-backed) the list pages and
    // dashboard already load — no extra network cost. Stay subscribed (via
    // takeUntil, NOT take(1)) so the bell resolves clicks against the CURRENT
    // feed: the service re-emits on proposal actions (accept/submit/etc.), and a
    // take(1) snapshot would go stale for the whole session, routing a status-
    // changed site to the wrong list. Matches the dashboard's live subscription.
    this.projectService
      .fetchData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => (this.projects = result));

    // Drive search visibility off the active route (initial + every navigation).
    this.updateShowSearch(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => this.updateShowSearch(e.urlAfterRedirects));

    // Same ProjectService.showClearSearchButton subscription as
    // header.component.ts (clears the term when a page resets the filter) plus
    // the flag info-navigation used to track for its own "Clear Search" button
    // — consolidated here since that button is retired in favor of the ✕.
    this.projectService.showClearSearchButton
      .pipe(takeUntil(this.destroy$))
      .subscribe((showButton: boolean) => {
        this.showClearSearchButton = showButton;
        if (!showButton) {
          this.searchTerm = '';
        }
      });

    interval(300000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchNotifications();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateShowSearch(url: string): void {
    const path = url.split(/[?#]/)[0].replace(/^\//, '');
    this.showSearch = this.searchRoutes.includes(path);
  }

  search(): void {
    if (this.searchTerm?.length !== 0) {
      this.toastr.info('Searching for address...');
      this.projectService.fetchSearchData(this.searchTerm, true);
      this.projectService.toggleClearSearchButton(true);
    }
  }

  clearSearch(): void {
    this.projectService.clearSearchData();
    this.projectService.toggleClearSearchButton(false);
  }

  fetchNotifications(): void {
    if (this.user?.id) {
      this.notificationService
        .getVendorNotifications(this.user.id)
        .subscribe((result: any) => {
          this.processNotificationResponse(result);
        });
    }
  }

  toggleNotificationsModal(): void {
    this.notificationsActive = !this.notificationsActive;
  }

  markAllAsRead(): void {
    this.notificationService
      .markNotificationRead(this.user.id ?? '', this.unreadNotifications)
      .subscribe((result: any) => {
        this.processNotificationResponse(result);
      });
  }

  markAsRead(notification: any): void {
    if (notification.status === 'UNREAD') {
      this.notificationService
        .markNotificationRead(this.user.id ?? '', [notification])
        .subscribe((result: any) => {
          this.processNotificationResponse(result);
        });
    }
  }

  markAsUnread(notification: any): void {
    if (notification.status === 'READ') {
      this.notificationService
        .markNotificationUnread(this.user.id ?? '', notification)
        .subscribe((result: any) => {
          this.processNotificationResponse(result);
        });
    }
  }

  private processNotificationResponse(result: any): void {
    if (result.length > 0) {
      this.unreadNotifications = result.filter(
        (notification: any) => notification.status === 'UNREAD'
      );
      this.readNotifications = result.filter(
        (notification: any) => notification.status === 'READ'
      );
    }
  }
}
